
import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { success, warning } from 'core/components/Notify';
import { useCommunicationSetupSlice } from 'features/CommunicationSetup/slice';
import { WHATSAPP_CONFIG } from '../constants';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';

declare global {
    interface Window {
        FB: any;
        fbAsyncInit: () => void;
    }
}

export const useWhatsAppAuth = () => {
    const dispatch = useDispatch();
    const { actions: commActions } = useCommunicationSetupSlice();
    const authData = useAuth();

    const [isFBSDKLoaded, setIsFBSDKLoaded] = useState(false);
    const [whatsAppSession, setWhatsAppSession] = useState<{
        code: string;
        phoneNumberId: string;
        wabaId: string;
    } | null>(null);
    
    const tempWhatsAppSession = useRef<{ phoneNumberId: string; wabaId: string } | null>(null);

    // Initialize Facebook SDK
    useEffect(() => {
        if (window.FB) {
            setIsFBSDKLoaded(true);
            return;
        }

        window.fbAsyncInit = function () {
            window.FB.init({
                appId: WHATSAPP_CONFIG.FACEBOOK_APP_ID,
                cookie: true,
                xfbml: true,
                version: WHATSAPP_CONFIG.API_VERSION
            });
            setIsFBSDKLoaded(true);
            console.log('✅ Facebook SDK loaded for WhatsApp');
        };

        if (!document.getElementById('facebook-jssdk')) {
            const script = document.createElement('script');
            script.id = 'facebook-jssdk';
            script.src = 'https://connect.facebook.net/en_US/sdk.js';
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
        }
    }, []);

    // Session logging message event listener
    useEffect(() => {
        const handleWhatsAppMessage = (event) => {
            if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") {
                return;
            }

            try {
                let data;
                try {
                    data = JSON.parse(event.data);
                } catch (e) {
                    return;
                }

                if (data.type === 'WA_EMBEDDED_SIGNUP') {
                    console.log('📱 WhatsApp Embedded Signup Event:', data);

                    // Meta sends different finish events per feature type: 'FINISH' for standard
                    // Cloud API onboarding, 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING' for the
                    // "Connect a WhatsApp Business app" (coexistence) flow, and 'FINISH_ONLY_WABA'
                    // when no phone is shared. Capture session info on any FINISH* variant —
                    // matching only 'FINISH' silently dropped the business-app flow's session,
                    // leaving phoneNumberId undefined so no fetch call ever fired.
                    if (typeof data.event === 'string' && data.event.startsWith('FINISH')) {
                        const { phone_number_id, waba_id } = data.data || {};
                        console.log('✅ WhatsApp Signup Finished - Session Info:', { event: data.event, phone_number_id, waba_id });
                        tempWhatsAppSession.current = { phoneNumberId: phone_number_id, wabaId: waba_id };
                    } else if (data.event === 'CANCEL') {
                        console.log('❌ WhatsApp Signup Cancelled');
                    } else if (data.event === 'ERROR') {
                        console.error('❌ WhatsApp Signup Error:', data.data);
                    }
                }
            } catch (error) {
                console.log('Non-JSON message received:', error);
            }
        };

        window.addEventListener('message', handleWhatsAppMessage);
        return () => window.removeEventListener('message', handleWhatsAppMessage);
    }, []);

    const handleWhatsAppAuthFlow = () => {
        try {
            if (!window.FB || !isFBSDKLoaded) {
                alert('Please wait, Facebook SDK is loading...');
                return;
            }

            tempWhatsAppSession.current = null;

            window.FB.login(
                function (response) {
                    console.log('WhatsApp Embedded Signup Response:', response);
                    if (response.status === 'connected' && response.authResponse) {
                        const code = response.authResponse.code;
                        const phoneNumberId = response.authResponse.sessionInfo?.phone_number_id || tempWhatsAppSession.current?.phoneNumberId;
                        const wabaId = response.authResponse.sessionInfo?.waba_id || tempWhatsAppSession.current?.wabaId;

                        if (phoneNumberId) {
                            console.log('✅ WhatsApp Authorization code:', code);
                            setWhatsAppSession({ code, phoneNumberId, wabaId });
                            success('WhatsApp Business connected successfully!', true);

                            dispatch(commActions.fetchCommunicationSetupListStart({
                                URL: 'whatsapp/list',
                                DATA: {
                                    BRANCH_ID: authData?.BRANCH_ID,
                                    PHONE_NUMBER_ID: phoneNumberId,
                                    WABA_ID: wabaId,
                                    SERVICE: 'wa'
                                },
                                ID: phoneNumberId,
                                action: 'VIEW_AND_LIST_API_CALL'
                            }));
                        } else {
                            warning('Please select or add a phone number to continue.', true);
                        }
                    } else {
                        console.log('❌ WhatsApp authorization cancelled');
                        warning('WhatsApp authorization was cancelled. Please try again.', true);
                    }
                },
                {
                    config_id: WHATSAPP_CONFIG.CONFIGURATION_ID,
                    response_type: 'code',
                    override_default_response_type: true,
                    extras: {
                        setup: {},
                        featureType: "whatsapp_business_app_onboarding",
                        sessionInfoVersion: "3"
                    }
                }
            );
        } catch (err) {
            console.error('Error during WhatsApp auth flow:', err);
        }
    };

    return {
        whatsAppSession,
        handleWhatsAppAuthFlow,
        isFBSDKLoaded
    };
};
